import {
    Directive,
    ElementRef,
    OnInit,
    Output,
    EventEmitter,
    Renderer2,
    HostListener,
    AfterViewInit,
    Input,
    OnDestroy
} from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { map, debounceTime, takeUntil } from 'rxjs/operators';

import { get } from 'lodash-es';

import { GoogleMapService } from '@core/services/google_map_service/google-map.service';
import { LocationApiService } from '@core/services/google_map_service/location-api.service';
import { ReverseGeoCodeResponse } from '../models/api-response/reverse-geocode-response';
import { GtmService } from '@app/core/services/analytics/gtm.service';

declare let google: any;

@Directive({
    selector: '[appGooglePlace]',
    standalone: true
})
export class GooglePlaceDirective implements OnInit, AfterViewInit, OnDestroy {
    @Input() mode: string;
    @Input() isHeader = false;
    @Output() hide: EventEmitter<any> = new EventEmitter();
    @Output() onSelect: EventEmitter<any> = new EventEmitter();

    _element: ElementRef;
    _autoCompleteServiceInstance: any = null;
    _appendListItem: any;
    predictions: any = [];

    placePredictionsMap: any = new Map();
    dropdownDOM: any;
    queryText: string;

    destroySubject$: Subject<void> = new Subject();

    constructor(
        private elRef: ElementRef,
        private renderer: Renderer2,
        private gtmService: GtmService,
        private gMapService: GoogleMapService,
        private locationApiService: LocationApiService
    ) {
        this._element = this.elRef;
    }

    ngOnInit() {
        this.gMapService.onMapLoad$.pipe(takeUntil(this.destroySubject$)).subscribe((isLoaded: boolean) => {
            if (isLoaded) {
                this.initAutocomplete();
                this.destroySubject$.next();
            }
        });
        this.disableBodyScroll();
    }

    ngAfterViewInit() {
        this.renderer.listen(this._element.nativeElement, 'focus', event => {
            if (event.currentTarget.value.length > 2 && this.dropdownDOM) {
                this.renderer.setStyle(this.dropdownDOM, 'display', 'block');
            }
        });
    }

    ngOnDestroy(): void {
        this.enableBodyScroll();
    }

    disableBodyScroll() {
        this.renderer.setStyle(document.body, 'overflow', 'hidden');
    }

    enableBodyScroll() {
        this.renderer.removeStyle(document.body, 'overflow');
    }

    _createListItem() {
        const _this = this;
        const mode = this.mode;
        const isHeader = this.isHeader;

        const parent = this.renderer.parentNode(this._element.nativeElement);
        this.dropdownDOM = this.renderer.createElement('div');
        const ulElement = this.renderer.createElement('ul');

        this.renderer.addClass(this.dropdownDOM, 'search_result-container');

        function createLiItem(sft: any) {
            const { main_text, secondary_text } = sft;
            const bold_text = sft.main_text.slice(0, get(sft, 'main_text_matched_substrings[0].length', 0));
            const remaining_main_text = sft.main_text.slice(
                get(sft, 'main_text_matched_substrings[0].length', 0),
                sft.main_text.length
            );

            return { main_text, bold_text, remaining_main_text, secondary_text };
        }

        function reverseGeocode(placeId): any {
            const geocoder = new google.maps.Geocoder();
            return new Promise(resolve => {
                geocoder.geocode({ placeId: placeId }, (results, status) => {
                    resolve({ results, status });
                });
            });
        }

        function updateDetails(results: any[]) {
            if (results[0]) {
                this.onSelect.emit(results[0]);
                this.renderer.setStyle(this.dropdownDOM, 'display', 'none');
            }
        }

        async function _onSelect() {
            try {
                let details: any[] = [];
                if (this.reverseGeocodeData) {
                    details = this.reverseGeocodeData.results;
                    _this.gtmService.logEvent('web_analytics', {
                        event_action: 'address',
                        event_label: 'Reverse Geocode info received from autocomplete API',
                        value1: 'Mode: ' + mode
                    });
                } else {
                    const response = (await this.locationApiService.reverseGeocode({
                        place_id: this.placeId,
                        input: this.queryText
                    })) as ReverseGeoCodeResponse;

                    if (response && response.meta.code === 200) {
                        details = response.data.geocode_info;
                        _this.gtmService.logEvent('web_analytics', {
                            event_action: 'address',
                            event_label: 'Reverse Geocode response received from get_reverse_geocode API',
                            value1: 'Mode: ' + mode
                        });
                    } else {
                        const geocoderResponse = await reverseGeocode(this.placeId);
                        details = geocoderResponse.results;
                        _this.gtmService.logEvent('web_analytics', {
                            event_action: 'address',
                            event_label: 'Reverse Geocode response received from geocoder',
                            value1: 'Mode: ' + mode
                        });
                    }
                }

                updateDetails.call(this, details);
            } catch (error) {
                console.log(error, 'error');

                // geocode response received from geocoder
                const geocoderResponse = await reverseGeocode(this.placeId);
                const details = geocoderResponse.results;
                _this.gtmService.logEvent('web_analytics', {
                    event_action: 'address',
                    event_label: 'Reverse Geocode response received from geocoder',
                    value1: 'Mode: ' + mode
                });
                updateDetails.call(this, details);
            }
        }

        return function (predictions) {
            // clear ul
            ulElement.innerHTML = '';

            for (let i = 0; i < predictions.length; i++) {
                // create the DOM element
                const li = this.renderer.createElement('li');

                // create text for the element

                const textObj = createLiItem(predictions[i].structured_formatting);
                const boldTextSpan = this.renderer.createElement('span');
                const boldText = this.renderer.createText(textObj.bold_text + textObj.remaining_main_text);
                this.renderer.appendChild(boldTextSpan, boldText);
                this.renderer.addClass(boldTextSpan, 'matched_text');

                const secondaryTextSpan = this.renderer.createElement('span');
                const secondaryText = this.renderer.createText(textObj.secondary_text);
                this.renderer.appendChild(secondaryTextSpan, secondaryText);
                this.renderer.addClass(secondaryTextSpan, 'secondary_text');

                const textContainer = this.renderer.createElement('div');
                this.renderer.addClass(textContainer, 'text-container');
                this.renderer.appendChild(textContainer, boldTextSpan);
                this.renderer.appendChild(textContainer, secondaryTextSpan);

                const iconContainer = this.renderer.createElement('span');
                const iconSpan = this.renderer.createElement('span');
                this.renderer.addClass(iconContainer, 'addressSVG');
                this.renderer.addClass(iconSpan, 'addressSVG__icon');
                this.renderer.addClass(iconSpan, 'addressSVG__icon--location');
                this.renderer.appendChild(iconContainer, iconSpan);

                // append text to li element
                this.renderer.appendChild(li, iconContainer);
                this.renderer.appendChild(li, textContainer);

                this.renderer.listen(
                    li,
                    'click',
                    _onSelect.bind({
                        renderer: this.renderer,
                        onSelect: this.onSelect,
                        dropdownDOM: this.dropdownDOM,
                        placeId: predictions[i].place_id,
                        queryText: this.queryText,
                        optionText: textObj.main_text,
                        // we can use reverse_geocode info from auto complete in header, since it dosen't require address form
                        reverseGeocodeData: isHeader ? predictions[i].reverse_geocode : null,
                        locationApiService: this.locationApiService
                    })
                );

                // append li to ul
                ulElement.appendChild(li);
            }

            // append ul to dropdown
            this.dropdownDOM.appendChild(ulElement);
            parent.appendChild(this.dropdownDOM);
        };
    }

    _afterfetch(predictions) {
        if (!predictions) {
            return;
        }
        this.predictions = predictions.filter(prediction => (prediction.place_id ? true : false));
        this._appendListItem(this.predictions);
    }

    _getAutocompleteServiceInstance() {
        const service = new google.maps.places.AutocompleteService();
        const sessionToken = new google.maps.places.AutocompleteSessionToken();

        return function (inputValue) {
            service.getPlacePredictions(
                {
                    input: inputValue,
                    sessionToken: sessionToken,
                    fields: ['address_component', 'geometry'],
                    componentRestrictions: { country: 'IN' }
                },
                predictions => {
                    this.placePredictionsMap.set(inputValue, predictions);
                    this._afterfetch(predictions);
                }
            );
        };
    }

    _fetchQueryPredictions(inputValue) {
        if (!this._autoCompleteServiceInstance) {
            this._autoCompleteServiceInstance = this._getAutocompleteServiceInstance();
        }
        this._autoCompleteServiceInstance(inputValue);
    }

    _fetchQueryPredictionsFromAPI(inputValue: string) {
        return new Promise((resolve, reject) => {
            this.locationApiService
                .getAutocompleteQuery(inputValue)
                .then((response: any) => {
                    if (response && response.meta && response.meta.code === 200) {
                        this.placePredictionsMap.set(inputValue, response.data.predictions);
                        this._afterfetch(response.data.predictions);
                    }
                    resolve(response);
                })
                .catch(errorResp => {
                    reject(errorResp.error);
                });
        });
    }

    initAutocomplete() {
        const inputElement = this._element.nativeElement;

        fromEvent(inputElement, 'keyup')
            .pipe(
                map((e: any) => e.target.value),
                debounceTime(600)
            )
            .subscribe(async inputValue => {
                if (!this._appendListItem) {
                    this._appendListItem = this._createListItem();
                }

                if (!this._shouldFetchData(inputValue)) {
                    return;
                }

                this.queryText = inputValue;

                try {
                    const response = (await this._fetchQueryPredictionsFromAPI(inputValue)) as any;
                    if (response && response.waiting) {
                        return;
                    }

                    if (!response || response.meta.code !== 200) {
                        this._fetchQueryPredictions(inputValue);
                    }
                } catch (error) {
                    this._fetchQueryPredictions(inputValue);
                    console.log(error);
                }
            });
    }

    private _shouldFetchData(inputValue: string): boolean {
        if (inputValue.length < 3) {
            this.renderer.setStyle(this.dropdownDOM, 'display', 'none');
            return false;
        }

        this.renderer.setStyle(this.dropdownDOM, 'display', 'block');
        const queryPredictionsValue = this.placePredictionsMap.get(inputValue);
        if (queryPredictionsValue) {
            this._afterfetch(queryPredictionsValue);
            return false;
        }
        return true;
    }

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement) {
        if (this.dropdownDOM) {
            const clickedInside = this.dropdownDOM.contains(targetElement) || this._element.nativeElement.contains(targetElement);
            if (!clickedInside) {
                this.renderer.setStyle(this.dropdownDOM, 'display', 'none');
            }
        }
    }
}
